import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Trash2, Edit, X, Info, ArrowUp, ArrowDown } from 'lucide-react';

const GROUP_TYPES = [
  { value: 'approval', label: 'Approval', description: 'Approval groups are used in document approval workflows. When a requisition, purchase order, or contract is routed to an approval group, the designated members receive a notification and must approve the document before it progresses. You can configure whether any one member can approve, or all members must approve.' },
  { value: 'rfx', label: 'RFx Management', description: 'RFx Management groups are responsible for running competitive sourcing events (RFI, RFQ, RFP, and Reverse Auctions). When a supplier asks a question on a sourcing event, all members of the assigned RFx group receive an email notification. They are responsible for answering questions, evaluating responses, and making award decisions.' },
  { value: 'vendor_management', label: 'Vendor Management', description: 'Vendor Management groups handle the supplier onboarding process. When a new supplier registers through the Supplier Portal, all members of this group receive an email notification to review and approve the registration. They also manage supplier data, compliance documentation, and performance reviews.' },
  { value: 'supplier_pricing', label: 'Supplier Pricing', description: 'Supplier Pricing groups manage price change requests from suppliers. When a supplier submits a request to change their pricing (individual item, category, or global percentage change), members of this group are notified and must review and approve or reject the request.' },
  { value: 'accounts', label: 'Accounts / Finance', description: 'Accounts and Finance groups handle financial operations including invoice approval, payment authorisation, three-way matching exceptions, and financial reporting. Members may also be involved in budget reviews and spend analysis.' },
];

const TYPE_COLORS = {
  approval: 'bg-blue-100 text-blue-800',
  rfx: 'bg-violet-100 text-violet-800',
  vendor_management: 'bg-amber-100 text-amber-800',
  supplier_pricing: 'bg-emerald-100 text-emerald-800',
  accounts: 'bg-rose-100 text-rose-800',
};

export function UserGroupsPanel() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editGroup, setEditGroup] = useState(null);
  const [form, setForm] = useState({ group_code: '', description: '', group_type: 'approval', members: [], active: true });
  const [filterType, setFilterType] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [groupUsage, setGroupUsage] = useState({});
  const [sortKey, setSortKey] = useState('group_code');
  const [sortDir, setSortDir] = useState('asc');

  const load = async () => {
    try {
      const [g, u] = await Promise.all([
        api.get('/api/admin/approval-groups'),
        api.get('/api/admin/users?page_size=200'),
      ]);
      setGroups(g.data.groups || []);
      setUsers(u.data.data || []);

      // Check usage for each group (for delete protection)
      const usage = {};
      for (const grp of (g.data.groups || [])) {
        const code = grp.group_code;
        let used = false;
        let reason = '';
        if ((grp.members || []).length > 0) { used = true; reason = `Has ${grp.members.length} member(s)`; }
        // Check if used in approval rules or RFx events
        try {
          const [rules, events] = await Promise.all([
            api.get('/api/admin/approval-rules').catch(() => ({ data: { rules: [] } })),
            api.get('/api/rfx/events?page_size=200').catch(() => ({ data: { data: [] } })),
          ]);
          const ruleUse = (rules.data.rules || []).some(r => (r.approver_groups || []).includes(code));
          const eventUse = (events.data.data || []).some(e => e.rfx_management_group === code);
          if (ruleUse) { used = true; reason = reason ? `${reason}; used in approval rules` : 'Used in approval rules'; }
          if (eventUse) { used = true; reason = reason ? `${reason}; assigned to RFx events` : 'Assigned to RFx events'; }
        } catch {}
        usage[code] = { used, reason };
      }
      setGroupUsage(usage);
    } catch { toast.error('Failed to load groups'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...(filterType ? groups.filter(g => g.group_type === filterType) : groups)].sort((a, b) => {
    let va = a[sortKey] || '';
    let vb = b[sortKey] || '';
    if (sortKey === 'members') { va = (a.members || []).length; vb = (b.members || []).length; }
    else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const SortHeader = ({ label, field, className = '' }) => (
    <th className={`text-left px-4 py-2.5 font-semibold cursor-pointer select-none hover:bg-slate-100 transition-colors ${className}`}
      onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === field && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  const saveGroup = async () => {
    if (!form.group_code.trim()) { toast.error('Group code is required'); return; }
    try {
      if (editGroup) {
        await api.put(`/api/admin/approval-groups/${editGroup}`, form);
        toast.success('Group updated');
      } else {
        await api.post('/api/admin/approval-groups', form);
        toast.success('Group created');
      }
      setEditGroup(null);
      setForm({ group_code: '', description: '', group_type: 'approval', members: [], active: true });
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed'); }
  };

  const deleteGroup = async (code) => {
    if (!window.confirm(`Delete group "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/approval-groups/${code}`);
      toast.success('Group deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const openEdit = (group) => {
    setEditGroup(group.group_code);
    setForm({ group_code: group.group_code, description: group.description || '', group_type: group.group_type || 'approval', members: group.members || [], active: group.active !== false });
  };

  const addMember = (userId) => { if (!form.members.includes(userId)) f('members', [...form.members, userId]); };
  const removeMember = (userId) => { f('members', form.members.filter(m => m !== userId)); };
  const getUserName = (id) => { const u = users.find(u => u.user_id === id || u.email === id); return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : id; };

  const filteredUsers = userSearch
    ? users.filter(u => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase()))
    : users.slice(0, 20);

  if (loading) return <p className="text-sm text-slate-400 py-8 text-center">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="user-groups-panel">
      <div>
        <h2 className="text-lg font-bold mb-1">User Groups</h2>
        <p className="text-sm text-slate-500">Manage groups across the system. Users can belong to multiple groups of different types. Groups control who receives notifications, manages sourcing events, approves documents, and handles supplier relationships.</p>
      </div>

      {/* Group Type Filter with Tooltips */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilterType('')} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!filterType ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-50'}`}>
          All ({groups.length})
        </button>
        {GROUP_TYPES.map(t => {
          const count = groups.filter(g => g.group_type === t.value).length;
          return (
            <div key={t.value} className="relative group/tip">
              <button onClick={() => setFilterType(filterType === t.value ? '' : t.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filterType === t.value ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-50'}`}>
                {t.label} ({count})
              </button>
              <div className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all pointer-events-none leading-relaxed">
                <p className="font-semibold mb-1">{t.label}</p>
                <p>{t.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Groups Table — sortable */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortHeader label="Group Code" field="group_code" />
            <SortHeader label="Type" field="group_type" />
            <SortHeader label="Description" field="description" />
            <SortHeader label="Members" field="members" className="text-center" />
            <th className="text-center px-4 py-2.5 font-semibold">Active</th>
            <th className="text-right px-4 py-2.5 font-semibold w-24">Actions</th>
          </tr></thead>
          <tbody>
            {sorted.map(g => {
              const usage = groupUsage[g.group_code] || {};
              const canDelete = !usage.used;
              return (
                <tr key={g.group_code} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold">{g.group_code}</td>
                  <td className="px-4 py-2.5"><Badge className={`text-[10px] ${TYPE_COLORS[g.group_type] || 'bg-slate-100 text-slate-700'}`}>{GROUP_TYPES.find(t => t.value === g.group_type)?.label || g.group_type || 'Approval'}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600">{g.description || '—'}</td>
                  <td className="px-4 py-2.5 text-center"><Badge variant="outline" className="text-[10px]">{(g.members || []).length}</Badge></td>
                  <td className="px-4 py-2.5 text-center">{g.active !== false ? <span className="text-emerald-600 text-xs font-medium">Yes</span> : <span className="text-slate-400 text-xs">No</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(g)} title="Edit group"><Edit className="w-3.5 h-3.5" /></Button>
                      <div className="relative group/del">
                        <Button size="sm" variant="ghost" className={`h-7 px-2 ${canDelete ? 'text-red-400 hover:text-red-600' : 'text-slate-200 cursor-not-allowed'}`}
                          onClick={() => canDelete && deleteGroup(g.group_code)} disabled={!canDelete}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        {!canDelete && (
                          <div className="absolute right-0 top-full mt-1 z-50 w-56 p-2 bg-slate-900 text-white text-[10px] rounded-lg shadow-xl opacity-0 invisible group-hover/del:opacity-100 group-hover/del:visible transition-all pointer-events-none leading-relaxed">
                            Cannot delete: {usage.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-xs">No groups found. Create one below.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Form */}
      <div className="border rounded-lg p-5 bg-slate-50/50">
        <h3 className="font-bold text-sm mb-3">{editGroup ? `Edit Group: ${editGroup}` : 'Create New Group'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-xs">Group Code *</Label>
            <Input value={form.group_code} onChange={e => f('group_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
              disabled={!!editGroup} placeholder="e.g. RFX-TEAM" className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Group Type *</Label>
            <Select value={form.group_type} onValueChange={v => f('group_type', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GROUP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.group_type && (
              <p className="text-[10px] text-slate-500 mt-1">{GROUP_TYPES.find(t => t.value === form.group_type)?.description?.substring(0, 120)}...</p>
            )}
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="What this group is for..." className="h-8 text-sm" />
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
          <Label className="text-xs">Active</Label>
        </div>

        {/* Members */}
        <div className="mb-4">
          <Label className="text-xs font-semibold">Members ({form.members.length})</Label>
          <div className="flex flex-wrap gap-1.5 mt-1 mb-2 min-h-[32px] border rounded-lg p-2 bg-white">
            {form.members.length === 0 && <span className="text-xs text-slate-400">No members added</span>}
            {form.members.map(m => (
              <Badge key={m} className="bg-indigo-50 text-indigo-700 text-xs pr-1 gap-1">
                {getUserName(m)}
                <button onClick={() => removeMember(m)} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
          <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users to add..." className="h-8 text-xs max-w-xs" />
          {userSearch && (
            <div className="border rounded-lg mt-1 max-h-32 overflow-y-auto bg-white">
              {filteredUsers.filter(u => !form.members.includes(u.user_id) && !form.members.includes(u.email)).map(u => (
                <button key={u.user_id || u.email} onClick={() => { addMember(u.user_id || u.email); setUserSearch(''); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 border-b last:border-0 flex items-center justify-between">
                  <span>{u.first_name} {u.last_name}</span>
                  <span className="text-slate-400">{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={saveGroup} className="bg-indigo-600 hover:bg-indigo-700">
            {editGroup ? 'Update Group' : 'Create Group'}
          </Button>
          {editGroup && (
            <Button size="sm" variant="outline" onClick={() => { setEditGroup(null); setForm({ group_code: '', description: '', group_type: 'approval', members: [], active: true }); }}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
